import moment from "moment"
import "moment/locale/es"
import { IntlShape } from "react-intl"
import { mod } from "./numbers"
import { addBusinessDays } from "date-fns"
import { LANGUAGE_EN } from "constants/language"
import { titleCapitalize } from "./text"

const SPANISH_DATE_FORMAT_WITH_MONTH = "D MMMM, YYYY"
const ENGLISH_DATE_FORMAT_WITH_MONTH = "MMM D, YYYY"
const SPANISH_DATE_FORMAT_WITH_SHORT_MONTH = "D MMM, YYYY"
const ENGLISH_DATE_FORMAT_WITH_SHORT_MONTH = "MMM D, YYYY"
const DAYS = "days"
const MONTHS = "months"
const YEAR_KEY = "year"
const YEARS = "years"
const MONTH_FORMAT = "MMMM"
const MONTH = "month"
const DAY = "day"

export const DATE_FORMAT = "YYYY-MM-DD"
export const DEFAULT_DATE_FORMAT = "L"
export const ONE_MONTH_AGO = -1
export const TWELVE_MONTHS_AGO = -12

export const MONDAY_NUMBER = 1
export const TUESDAY_NUMBER = 2
export const WEDNESDAY_NUMBER = 3
export const THURSDAY_NUMBER = 4
export const FRIDAY_NUMBER = 5
export const SATURDAY_NUMBER = 6
export const SUNDAY_NUMBER = 7
const SUNDAY_NUMBER_AS_ZERO = 0
export const YEAR = moment().format("YYYY")

export const CURRENT_MONTH = "CURRENT_MONTH"
export const CURRENT_QUARTER = "CURRENT_QUARTER"
export const CURRENT_YEAR = "CURRENT_YEAR"
export const CURRENT_DAYS = "CURRENT_DAYS"
export const CURRENT_WEEK = "CURRENT_WEEK"
export const PHRONESIS_DEFAULT_DATE_FORMAT = "DD/MM/yyyy"

const weekDayNamesTranslationId: Record<number, string> = {
	[MONDAY_NUMBER]: "phronesis.common.weekday.monday",
	[TUESDAY_NUMBER]: "phronesis.common.weekday.tuesday",
	[WEDNESDAY_NUMBER]: "phronesis.common.weekday.wednesday",
	[THURSDAY_NUMBER]: "phronesis.common.weekday.thursday",
	[FRIDAY_NUMBER]: "phronesis.common.weekday.friday",
	[SATURDAY_NUMBER]: "phronesis.common.weekday.saturday",
	[SUNDAY_NUMBER]: "phronesis.common.weekday.sunday",
}

export const parseDayToISOFormat = (day: number) =>
	day !== SUNDAY_NUMBER_AS_ZERO ? day : SUNDAY_NUMBER

export const getWeekDayName = (intl: IntlShape, weekDay: number) => {
	return intl.formatMessage({ id: weekDayNamesTranslationId[weekDay] })
}

export const getWeekdays = (intl: IntlShape) => {
	return [
		intl.formatMessage({ id: weekDayNamesTranslationId[MONDAY_NUMBER] }),
		intl.formatMessage({ id: weekDayNamesTranslationId[TUESDAY_NUMBER] }),
		intl.formatMessage({ id: weekDayNamesTranslationId[WEDNESDAY_NUMBER] }),
		intl.formatMessage({ id: weekDayNamesTranslationId[THURSDAY_NUMBER] }),
		intl.formatMessage({ id: weekDayNamesTranslationId[FRIDAY_NUMBER] }),
		intl.formatMessage({ id: weekDayNamesTranslationId[SATURDAY_NUMBER] }),
		intl.formatMessage({ id: weekDayNamesTranslationId[SUNDAY_NUMBER] }),
	]
}

export const getFiveBusinessDaysFromToday = () => addBusinessDays(getToday(), 5)

export const getMessageWeekDay = (day: number, intl: IntlShape) => {
	const weekDay = [
		"monday",
		"tuesday",
		"wednesday",
		"thursday",
		"friday",
		"saturday",
		"sunday",
	]
	return intl.formatMessage({
		id: `phronesis.common.weekday.${weekDay[day - 1]}`,
	})
}

export const formatDate = (date: Date, format: string, locale?: string) => {
	if (locale) {
		return moment(date).locale(locale).format(format)
	} else {
		return moment(date).locale(LANGUAGE_EN).format(format)
	}
}

export const resolveMonthKey = (monthNumber: number) => {
	return months[monthNumber - 1]
}

export const resolveMonthShortKey = (monthNumber: number) => {
	return `${months[monthNumber - 1]}.short`
}

export type YearMonth = {
	year: number
	month: number
}

export const currentYearMonth = (): YearMonth => {
	return dateToYearMonth(new Date())
}

export const currentYearMonthPlus = (months: number): YearMonth => {
	const current = currentYearMonth()
	return yearMonthPlusMonths(current, months)
}

export const yearMonthPlusMonths = (
	{ year, month }: YearMonth,
	months: number
) => {
	const newMonth = mod(month - 1 + months, 12) + 1
	const newYear = year + Math.floor((month - 1 + months) / 12)
	return { year: newYear, month: newMonth }
}

export const yearMonthToDate = ({ year, month }: YearMonth) => {
	return new Date(year, month - 1, 1)
}

export const dateToYearMonth = (date: Date) => {
	return { year: date.getFullYear(), month: date.getMonth() + 1 }
}

export const stringToYearMonth = (date: string): YearMonth => {
	const dateParts = date.split("-")
	return {
		year: parseInt(dateParts[0]),
		month: parseInt(dateParts[1]),
	}
}

export type RangeDate = {
	startDate: Date
	endDate: Date
}

export const yearMonthRangeToDateRange = (
	from: YearMonth,
	to: YearMonth
): RangeDate => {
	const startDate = yearMonthToDate(from)
	const endDate = moment(yearMonthToDate(to)).endOf(MONTH).toDate()
	return { startDate, endDate }
}

const months = [
	"phronesis.common.months.january",
	"phronesis.common.months.february",
	"phronesis.common.months.march",
	"phronesis.common.months.april",
	"phronesis.common.months.may",
	"phronesis.common.months.june",
	"phronesis.common.months.july",
	"phronesis.common.months.august",
	"phronesis.common.months.september",
	"phronesis.common.months.october",
	"phronesis.common.months.november",
	"phronesis.common.months.december",
]

export const getToday = () => moment().toDate()
export const addBusinessDaysFromToday = (businessDaysToAdd: number) =>
	addBusinessDays(getToday(), businessDaysToAdd)
export const getThreeDaysBeforeToday = () => moment().subtract(3, DAYS).toDate()
export const getYesterday = () => moment().subtract(1, DAYS).toDate()
export const getTomorrow = () => moment().add(1, DAYS).toDate()
export const getWeekBefore = () =>
	moment().subtract(1, DAYS).subtract(7, DAYS).toDate()
export const getMonthBefore = () =>
	moment().subtract(1, DAYS).subtract(1, MONTHS).toDate()
export const getDatabaseMonthDifference = () =>
	moment().subtract(92, DAYS).toDate()
export const getMonthAfter = () => moment().add(1, DAYS).add(1, MONTHS).toDate()
export const getThreeMonthsBefore = () =>
	moment().subtract(1, DAYS).subtract(3, MONTHS).toDate()
export const getSixMonthsBefore = () =>
	moment().subtract(1, DAYS).subtract(6, MONTHS).toDate()
export const getYearToDay = () =>
	moment().subtract(1, DAYS).startOf(YEAR_KEY).toDate()
export const getYearBefore = () =>
	moment().subtract(1, DAYS).subtract(1, YEARS).toDate()
export const getLastMonthRange = (): RangeDate => ({
	startDate: getMonthBefore(),
	endDate: getYesterday(),
})
export const getDiffInDays = (endDate: Date, startDate: Date) =>
	moment(endDate).diff(startDate, "days")

export const addDaysToDate = (date: Date, maxInterval: number): Date =>
	moment(date).add(maxInterval, DAY).toDate()

export const getMonthName = (date: Date, locale?: string) =>
	moment(date)
		.locale(locale ? locale : LANGUAGE_EN)
		.format(MONTH_FORMAT)

export const dateFormat: { [key: string]: string } = {
	es: "DD/MM/yyyy",
	en: "MM/DD/yyyy",
}

export const dateTimeFormat: { [key: string]: string } = {
	es: "DD/MM/yyyy HH:mm",
	en: "MM/DD/yyyy HH:mm",
}

const formatDateWithDifferentFormats = (dateFormat: {
	[key: string]: string
}) => (date: Date, locale: string = LANGUAGE_EN) =>
	moment(date).locale(locale).format(dateFormat[locale])

export const formatDateWithMonth = formatDateWithDifferentFormats({
	es: SPANISH_DATE_FORMAT_WITH_MONTH,
	en: ENGLISH_DATE_FORMAT_WITH_MONTH,
})

const formatDateWithShortMonthLarge = formatDateWithDifferentFormats({
	es: SPANISH_DATE_FORMAT_WITH_SHORT_MONTH,
	en: ENGLISH_DATE_FORMAT_WITH_SHORT_MONTH,
})

export const formatDateWithShortMonth = (date: Date, locale?: string) => {
	const dateFormatted = formatDateWithShortMonthLarge(date, locale)
	return locale === "es" ? dateFormatted.replace(".", "") : dateFormatted
}

export const dateFormatSalesChart: { [key: string]: string } = {
	es: "%d-%m",
	en: "%m-%d",
}

export const resolveMonthName = (
	{ year, month }: YearMonth,
	intl: IntlShape,
	abbreviatedMonth = false,
	abbreviatedYear = false
): string => {
	const monthIntlId = getMonthKey(month, abbreviatedMonth)
	if (!monthIntlId) {
		return ""
	}

	const formattedMonth = intl.formatMessage({ id: monthIntlId })
	const formattedYear = abbreviatedYear ? year % 100 : year
	return `${formattedMonth} ${formattedYear}`
}

const getMonthKey = (month: number, short = false): string => {
	return short ? resolveMonthShortKey(month) : resolveMonthKey(month)
}

export const getWeekDayNumberFrom1To7 = (date: Date) =>
	date.getDay() === SUNDAY_NUMBER_AS_ZERO ? SUNDAY_NUMBER : date.getDay()

export const getSevenDaysBefore = () => moment().subtract(10, DAYS).toDate()

export const getShortMonth = (date: string, intl: IntlShape) => {
	const parsedDate = moment(date, DATE_FORMAT).toDate()
	return titleCapitalize(intl.formatDate(parsedDate, { month: "short" }))
}
